import styled from "@emotion/styled";
import { AddRounded, EditRounded, DeleteRounded, VisibilityRounded } from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	Divider,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useEffect, useState } from "react";

import RenderInputField from "components/ReusableComponents/RenderInputField/RenderInputField";
import { getColDefaultValue } from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { arrType, inputTypes, selectOptionsKeyName, getRelProtectedFileSignedUrl } from "data/globalData";
import axios from "axios";

import { departments } from "data/authData";
import {
	getTrimMasterPayloadWithItemDescription,
	trimCategoryColumn,
	trimCategoryDropdownsObj,
	trimSubCategoryDropdowns,
	trimsMasterColumnsObj,
	trimFileTypes,
	isValidTrimImageUrl,
} from "../data";
import {
	TrimCommonInputs,
	TrimInterliningExtraInputs,
	TrimLifecycleActions,
	TrimMainInputs,
	TrimZipperExtraInputs,
} from "../CreateModal/data";
import SupplierRowModal from "../CreateModal/SupplierRowModal/SupplierRowModal";

const createRowKey = () => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif", "bmp", "svg"];
const PDF_EXTENSION = "pdf";

const getFileExtension = (fileNameOrPath = "") => {
	const name = String(fileNameOrPath).split("?")[0].split("#")[0];
	const baseName = name.split("/").pop() || "";
	const parts = baseName.split(".");
	if (parts.length < 2) return "";
	return parts.pop().toLowerCase();
};

const getReviewableDevFileType = ({ file, path } = {}) => {
	if (file?.type) {
		if (file.type.startsWith("image/")) return "image";
		if (file.type === "application/pdf") return "pdf";
	}
	const ext = getFileExtension(file?.name || path || "");
	if (IMAGE_EXTENSIONS.includes(ext)) return "image";
	if (ext === PDF_EXTENSION) return "pdf";
	return null;
};

const cloneSupplierRow = (row = {}, fallbackKey) => ({
	...row,
	__rowKey: row.trim_supplier_code ?? row.id ?? fallbackKey ?? createRowKey(),
	styleTrimsSupplierSamples: Array.isArray(row?.styleTrimsSupplierSamples) ? [...row.styleTrimsSupplierSamples] : [],
});

const stripSupplierClientFields = (row = {}) => {
	const { __rowKey, __isNew, __isDeleted, is_new, is_updated, createdAt, updatedAt, styleTrimsSuppliers, ...rest } = row;
	return rest;
};

const UpdateModal = ({ open, setOpen, handleSaveFunction = () => {}, selectedRow, setSelectedRow, blockSupplierDetails = false, disableEdit = false }) => {
	const { user } = AuthState();
	const { snackBar, setSnackBarOpen, setSnackBar, setLoadingOpen } = GlobalState();
	const VisuallyHiddenInput = styled("input")({
		clip: "rect(0 0 0 0)",
		clipPath: "inset(50%)",
		height: 1,
		overflow: "hidden",
		position: "absolute",
		bottom: 0,
		left: 0,
		whiteSpace: "nowrap",
		width: 1,
	});

	const defPageData = [trimCategoryColumn, ...Object.values(trimsMasterColumnsObj)]
		.map((col) => ({ [col.key]: getColDefaultValue(col) }))
		.reduce((acc, row) => ({ ...acc, ...row }), {});

	const [pageData, setPageData] = useState(defPageData);
	const [errorObj, setErrorObj] = useState({});
	const [supplierRows, setSupplierRows] = useState([]);
	const [deletedSupplierRows, setDeletedSupplierRows] = useState([]);
	const [devFile, setDevFile] = useState(null);
	const [imageLoadError, setImageLoadError] = useState(false);
	const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
	const [devFilePreview, setDevFilePreview] = useState({ open: false, url: "", type: null, isBlob: false });
	const [supplierModalOpen, setSupplierModalOpen] = useState(false);
	const [supplierModalMode, setSupplierModalMode] = useState("create");
	const [selectedSupplierRow, setSelectedSupplierRow] = useState(null);

	const withItemDescription = (row) => (disableEdit ? row : getTrimMasterPayloadWithItemDescription(row));

	const updatePageData = (nextValueOrUpdater) => {
		setPageData((prev) => {
			const nextRow = typeof nextValueOrUpdater === "function" ? nextValueOrUpdater(prev) : nextValueOrUpdater;
			return withItemDescription(nextRow);
		});
	};

	useEffect(() => {
		if (!open || !selectedRow) return;

		const nextRow = { ...defPageData, ...selectedRow };
		const nextSupplierRows = Array.isArray(selectedRow?.styleTrimsSuppliers)
			? selectedRow.styleTrimsSuppliers.map((row, index) =>
					cloneSupplierRow(row, `${index}_${row?.trim_supplier_code ?? createRowKey()}`),
			  )
			: [];

		setPageData(withItemDescription(nextRow));
		setImageLoadError(false);
		setSupplierRows(nextSupplierRows);
		setDeletedSupplierRows([]);
	}, [open, selectedRow, disableEdit]);

	useEffect(() => {
		setImageLoadError(false);
	}, [pageData?.url]);

	const closeDevFilePreview = () => {
		setDevFilePreview((prev) => {
			if (prev.isBlob && prev.url) URL.revokeObjectURL(prev.url);
			return { open: false, url: "", type: null, isBlob: false };
		});
	};

	const handleCloseModal = () => {
		setOpen(false);
		setErrorObj({});
		setPageData(defPageData);
		setSupplierRows([]);
		setDeletedSupplierRows([]);
		setDevFile(null);
		setImageLoadError(false);
		setImagePreviewOpen(false);
		closeDevFilePreview();
		setSelectedRow();
		setSelectedSupplierRow(null);
		setSupplierModalOpen(false);
	};

	const handleTrimImageSelect = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = (event) => {
			setPageData((prev) => ({ ...prev, file, url: event.target?.result || "" }));
		};
		reader.onerror = () => {
			setImageLoadError(true);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "error",
				message: "Unable to preview trim image",
			});
		};
		reader.readAsDataURL(file);
		e.target.value = "";
	};

	const uploadTrimImage = async () => {
		if (!pageData?.file) {
			const existingUrl = pageData?.url ?? "";
			if (existingUrl.startsWith("blob:") || !isValidTrimImageUrl(existingUrl)) return "";
			return existingUrl;
		}

		const formData = new FormData();
		formData.append("file", pageData.file);
		formData.append("fileMeta", JSON.stringify({ department: departments.SOURCING.key, file_type: trimFileTypes.TRIM_IMAGES }));

		const uploadToBucket = await axios.post("/api/upload/single_upload_to_public_bucket", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});

		const result = uploadToBucket?.data?.result;
		const bucketUrl = result?.publicUrl ?? result?.url ?? "";
		if (!bucketUrl) {
			throw new Error("Trim image upload failed");
		}

		return bucketUrl;
	};

	const handleDevFileDownload = async () => {
		try {
			setLoadingOpen(true);
			const fileUrl = await getRelProtectedFileSignedUrl(pageData?.development_file);
			if (fileUrl) {
				window.open(fileUrl, "_blank");
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, message: "File Downloaded Successfully", severity: "success" });
			}
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Failed to download file", severity: "error" });
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleDevFileReview = async () => {
		try {
			if (devFile) {
				const type = getReviewableDevFileType({ file: devFile });
				if (!type) return;
				const blobUrl = URL.createObjectURL(devFile);
				setDevFilePreview((prev) => {
					if (prev.isBlob && prev.url) URL.revokeObjectURL(prev.url);
					return { open: true, url: blobUrl, type, isBlob: true };
				});
				return;
			}

			const type = getReviewableDevFileType({ path: pageData?.development_file });
			if (!type || !pageData?.development_file) return;

			setLoadingOpen(true);
			const fileUrl = await getRelProtectedFileSignedUrl(pageData.development_file);
			if (!fileUrl) throw new Error("Unable to generate file preview url");
			setDevFilePreview((prev) => {
				if (prev.isBlob && prev.url) URL.revokeObjectURL(prev.url);
				return { open: true, url: fileUrl, type, isBlob: false };
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Failed to preview file", severity: "error" });
		} finally {
			setLoadingOpen(false);
		}
	};

	const uploadDevelopmentFile = async () => {
		if (!devFile) return null;
		const fileFormData = new FormData();
		fileFormData.append("files", devFile);
		fileFormData.append(
			"fileMeta",
			JSON.stringify({ department: departments.SOURCING.key, file_type: trimFileTypes.TRIM_DEV_FILES }),
		);
		const res = await axios.post("/api/upload/multiple_upload_to_rel_bucket", fileFormData, {
			headers: { "content-type": "multipart/form-data" },
		});
		return res.data.result?.[0] ?? null;
	};

	const handleSupplierSave = (rowPayload) => {
		const nextRow = {
			...rowPayload,
			__rowKey: rowPayload?.__rowKey ?? rowPayload?.trim_supplier_code ?? rowPayload?.id ?? createRowKey(),
		};
		setSupplierRows((prev) => {
			const index = prev.findIndex(
				(row) =>
					row.__rowKey === nextRow.__rowKey ||
					(row.trim_supplier_code && row.trim_supplier_code === nextRow.trim_supplier_code),
			);
			if (index >= 0) {
				const existing = prev[index];
				const copy = [...prev];
				copy[index] = {
					...existing,
					...nextRow,
					...(existing.is_new ? {} : { is_updated: true }),
				};
				return copy;
			}
			return [...prev, { ...nextRow, is_new: true }];
		});
		setSelectedSupplierRow(null);
		setSupplierModalOpen(false);
	};

	const handleDeleteSupplierRow = (row) => {
		if (!row.is_new && row.trim_supplier_code) {
			setDeletedSupplierRows((prev) => [...prev, stripSupplierClientFields(row)]);
		}
		setSupplierRows((prev) => prev.filter((item) => item.__rowKey !== row.__rowKey));
	};

	const buildSupplierPayloads = () => {
		if (blockSupplierDetails) {
			return {
				styleTrimsSuppliersToCreate: [],
				styleTrimsSuppliersToUpdate: [],
				styleTrimsSuppliersToDelete: [],
			};
		}

		const createRows = supplierRows
			.filter((row) => row.is_new)
			.map((row) => {
				const { id, is_new, __rowKey, ...rest } = stripSupplierClientFields(row);
				return { ...rest, trim_code: pageData?.trim_code, created_by: user?.email ?? "", last_updated_by: user?.email ?? "" };
			});

		const updateRows = supplierRows
			.filter((row) => !row.is_new && row.is_updated)
			.map((row) => {
				const { is_updated, __rowKey, ...rest } = stripSupplierClientFields(row);
				return { ...rest, trim_code: pageData?.trim_code, last_updated_by: user?.email ?? "" };
			});

		const deleteRows = deletedSupplierRows.map((row) => ({ ...row, trim_code: pageData?.trim_code }));

		return {
			styleTrimsSuppliersToCreate: createRows,
			styleTrimsSuppliersToUpdate: updateRows,
			styleTrimsSuppliersToDelete: deleteRows,
		};
	};

	const handleSaveClick = () => {
		let errObj = {};

		[trimCategoryColumn, ...Object.values(trimsMasterColumnsObj)].forEach((col) => {
			if (col.input_type === inputTypes.AUTO_GENERATE.key) return;
			if (col.constraints?.NOTNULL && !pageData[col.key]) {
				errObj = { ...errObj, [col.key]: `${col.name} is required` };
			}
		});

		if (Object.keys(errObj).length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "warning",
				message: "Please fill the required fields",
			});
			setErrorObj(errObj);
			return;
		}

		setErrorObj({});
		(async () => {
			setLoadingOpen(true);
			try {
				const uploadedUrl = await uploadTrimImage();
				const developmentFileResult = await uploadDevelopmentFile();
				const payload = withItemDescription({
					...pageData,
					last_updated_by: user?.email ?? "",
				});
				if (uploadedUrl) payload.url = uploadedUrl;
				else if (payload.url?.startsWith("blob:")) delete payload.url;
				delete payload.file;
				delete payload.styleTrimsSuppliers;
				if (developmentFileResult) payload.development_file = developmentFileResult?.url ?? developmentFileResult;

				const supplierPayloads = buildSupplierPayloads();
				const savePayload = {
					...payload,
					...supplierPayloads,
				};

				if (typeof handleSaveFunction === "function") await handleSaveFunction(savePayload);
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					severity: "error",
					message: "Unable to save trim master",
				});
			} finally {
				setLoadingOpen(false);
			}
		})();
	};

	const openEditSupplierRow = (row) => {
		setSelectedSupplierRow(row);
		setSupplierModalMode("update");
		setSupplierModalOpen(true);
	};

	const openCreateSupplierRow = () => {
		setSelectedSupplierRow(null);
		setSupplierModalMode("create");
		setSupplierModalOpen(true);
	};

	const hasImage = isValidTrimImageUrl(pageData?.url) && !imageLoadError;
	const reviewableDevFileType = getReviewableDevFileType({
		file: devFile,
		path: pageData?.development_file,
	});

	return (
		<Dialog
			fullScreen
			maxWidth="xl"
			open={open}
			onClose={handleCloseModal}
			scroll="paper"
			PaperProps={{
				sx: {
					bgcolor: grey[50],
					height: "92vh",
					mt: "8vh",
					overflow: "hidden",
				},
			}}>
			<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
				<Box
					sx={{
						px: 2,
						py: 1.5,
						borderBottom: `1px solid ${grey[200]}`,
						bgcolor: "#fff",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 1,
					}}>
					<Box>
						<Typography variant="h6" fontWeight={800}>
							{disableEdit ? "View Trim Details" : "Update Trim Details"} - {selectedRow?.trim_code ?? ""}
						</Typography>
					</Box>
					<Box display="flex" gap={1} flexWrap="wrap">
						<Button variant="outlined" onClick={handleCloseModal}>
							{disableEdit ? "Close" : "Cancel"}
						</Button>
						{!disableEdit && (
							<Button variant="contained" onClick={handleSaveClick}>
								Save
							</Button>
						)}
					</Box>
				</Box>

				<Box
					sx={{
						flex: 1,
						display: "grid",
						gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1.1fr)" },
						gap: 2,
						p: 2,
						overflow: "hidden",
					}}>
					<Box
						sx={{
							bgcolor: "#fff",
							border: `1px solid ${grey[200]}`,
							borderRadius: 2,
							p: 2,
							overflow: "auto",
						}}>
						<Typography variant="subtitle1" fontWeight={800} mb={1.5}>
							Master Details
						</Typography>
						<Box display="flex" width="100%" rowGap={2} flexWrap="wrap" columnGap="3%">
							<Stack width="140px" spacing={0.75} alignItems="center">
								<Box
									width="140px"
									height="160px"
									border={`1px solid ${grey[300]}`}
									borderRadius={1}
									display="flex"
									alignItems="center"
									justifyContent="center"
									overflow="hidden"
									position="relative"
									sx={{
										backgroundColor: grey[50],
										cursor: !hasImage && !disableEdit ? "pointer" : "default",
									}}
									{...(!hasImage && !disableEdit ? { component: "label" } : {})}>
									{hasImage ? (
										<img
											src={pageData.url}
											alt="trim"
											onError={() => setImageLoadError(true)}
											style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute" }}
										/>
									) : (
										<Stack alignItems="center" spacing={0.5}>
											<AddRounded />
											<Typography variant="caption">Add Image</Typography>
										</Stack>
									)}

									{!hasImage && !disableEdit && (
										<VisuallyHiddenInput type="file" accept="image/*" onChange={handleTrimImageSelect} />
									)}
								</Box>

								{hasImage && (
									<Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
										{!disableEdit && (
											<Tooltip title="Edit image">
												<IconButton size="small" component="label" color="primary">
													<EditRounded fontSize="small" />
													<VisuallyHiddenInput type="file" accept="image/*" onChange={handleTrimImageSelect} />
												</IconButton>
											</Tooltip>
										)}
										<Tooltip title="Preview image">
											<IconButton size="small" color="primary" onClick={() => setImagePreviewOpen(true)}>
												<VisibilityRounded fontSize="small" />
											</IconButton>
										</Tooltip>
									</Box>
								)}
							</Stack>

							<Box width="calc(100% - 140px - 5%)">
								<TrimMainInputs data={pageData} setData={updatePageData} errorObj={errorObj} mode="update" disableEdit={disableEdit} />
							</Box>
							<TrimCommonInputs data={pageData} setData={updatePageData} errorObj={errorObj} mode="update" disableEdit={disableEdit} />

							<Box width="100%" mt={1}>
								<Divider orientation="horizontal" flexItem />
							</Box>

							{/* Development File */}
							<Box width="100%">
								<Typography variant="body2" fontWeight={600} mb={0.5}>
									Development File
								</Typography>
								<Box display="flex" alignItems="center" gap={1}>
									{!disableEdit && (
										<Button variant="outlined" size="small" component="label">
											{devFile || pageData?.development_file ? "Change File" : "Upload File"}
											<VisuallyHiddenInput
												type="file"
												onChange={(e) => {
													const f = e.target.files?.[0];
													if (f) setDevFile(f);
												}}
											/>
										</Button>
									)}
									{devFile ? (
										<>
											<Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
												{devFile.name}
											</Typography>
											{reviewableDevFileType && (
												<Button size="small" variant="contained" onClick={handleDevFileReview}>
													View
												</Button>
											)}
											{!disableEdit && (
												<Button size="small" color="error" onClick={() => setDevFile(null)}>
													Remove
												</Button>
											)}
										</>
									) : pageData?.development_file ? (
										<Box display="flex" alignItems="center" gap={1}>
											<Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 240 }}>
												{pageData.development_file.split("/").pop()}
											</Typography>
											{reviewableDevFileType && (
												<Button size="small" variant="contained" onClick={handleDevFileReview}>
													View
												</Button>
											)}
											<Button size="small" variant="outlined" onClick={handleDevFileDownload}>
												Download
											</Button>
										</Box>
									) : (
										<Typography variant="caption" color={grey[400]}>
											No file selected
										</Typography>
									)}
								</Box>
							</Box>

							<Box>
								{pageData?.[trimCategoryColumn.key] === trimCategoryDropdownsObj?.ZIPPER?.key && (
									<TrimZipperExtraInputs
										data={pageData}
										setData={updatePageData}
										errorObj={errorObj}
										mode="create"
										disableEdit={disableEdit}
									/>
								)}
								{pageData?.[trimCategoryColumn.key] === trimCategoryDropdownsObj?.INTERLINING?.key && (
									<TrimInterliningExtraInputs
										data={pageData}
										setData={updatePageData}
										errorObj={errorObj}
										mode="create"
										disableEdit={disableEdit}
									/>
								)}
							</Box>
						</Box>
					</Box>

					<Box
						sx={{
							bgcolor: "#fff",
							border: `1px solid ${grey[200]}`,
							borderRadius: 2,
							p: 2,
							display: "flex",
							flexDirection: "column",
							minHeight: 0,
							rowGap: 1,
						}}>
						<TrimLifecycleActions data={pageData} setData={updatePageData} disableEdit={disableEdit} />

						{!blockSupplierDetails && (
							<>
								<Box width="100%" mt={1}>
									<Divider orientation="horizontal" flexItem />
								</Box>

								<Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} gap={1}>
									<Box>
										<Typography variant="subtitle1" fontWeight={800}>
											Supplier and Pricing Details
										</Typography>
										<Typography variant="body2" color={grey[600]}>
											Manage supplier-level pricing for this trim.
										</Typography>
									</Box>
									<Button
										variant="contained"
										startIcon={<AddRounded />}
										onClick={openCreateSupplierRow}
										disabled={disableEdit}>
										Add Row
									</Button>
								</Box>

								<TableContainer
									component={Paper}
									variant="outlined"
									sx={{
										flex: 1,
										minHeight: 0,
										overflow: "auto",
									}}>
									<Table stickyHeader size="small">
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontWeight: 700 }}>Supplier</TableCell>
												<TableCell sx={{ fontWeight: 700 }} align="right">
													Price
												</TableCell>
												<TableCell sx={{ fontWeight: 700 }}>Currency</TableCell>
												<TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
												<TableCell sx={{ fontWeight: 700 }}>Ref. Code</TableCell>
												<TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
												<TableCell sx={{ fontWeight: 700 }} align="center">
													Actions
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{supplierRows.length ? (
												supplierRows.map((row) => (
													<TableRow key={row.__rowKey} hover>
														<TableCell>{row.supplier || "-"}</TableCell>
														<TableCell align="right">{row.unit_price ?? 0}</TableCell>
														<TableCell>{row.currency || "-"}</TableCell>
														<TableCell>{row.unit || "-"}</TableCell>
														<TableCell>{row.supplier_reference_code || "-"}</TableCell>
														<TableCell>{row.remarks || "-"}</TableCell>
														<TableCell align="center">
															<IconButton size="small" onClick={() => openEditSupplierRow(row)} disabled={disableEdit}>
																<EditRounded fontSize="small" />
															</IconButton>
															<IconButton
																size="small"
																color="error"
																onClick={() => handleDeleteSupplierRow(row)}
																disabled={disableEdit}>
																<DeleteRounded fontSize="small" />
															</IconButton>
														</TableCell>
													</TableRow>
												))
											) : (
												<TableRow>
													<TableCell colSpan={7} align="center">
														No supplier rows added yet.
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</>
						)}
					</Box>
				</Box>
			</Box>
			{supplierModalOpen && !blockSupplierDetails && (
				<SupplierRowModal
					open={supplierModalOpen}
					setOpen={setSupplierModalOpen}
					mode={supplierModalMode}
					selectedRow={selectedSupplierRow}
					onSave={handleSupplierSave}
					selectedTrim={pageData}
				/>
			)}
			<Dialog open={imagePreviewOpen} onClose={() => setImagePreviewOpen(false)} maxWidth="md">
				<Box p={1.5} display="flex" flexDirection="column" alignItems="center" gap={1}>
					<img
						src={pageData?.url}
						alt="trim preview"
						style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", borderRadius: 4 }}
					/>
					<Button variant="outlined" size="small" onClick={() => setImagePreviewOpen(false)}>
						Close
					</Button>
				</Box>
			</Dialog>
			<Dialog
				open={devFilePreview.open}
				onClose={closeDevFilePreview}
				maxWidth={devFilePreview.type === "pdf" ? "xl" : "md"}
				fullWidth
				PaperProps={
					devFilePreview.type === "pdf"
						? { sx: { width: "95vw", maxWidth: 1400, height: "92vh" } }
						: undefined
				}>
				<Box
					p={1.5}
					display="flex"
					flexDirection="column"
					alignItems="center"
					gap={1}
					sx={devFilePreview.type === "pdf" ? { height: "100%", boxSizing: "border-box" } : undefined}>
					{devFilePreview.type === "image" ? (
						<img
							src={devFilePreview.url}
							alt="development file preview"
							style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", borderRadius: 4 }}
						/>
					) : (
						<iframe
							title="development file preview"
							src={devFilePreview.url}
							style={{ width: "100%", flex: 1, minHeight: 0, height: "100%", border: "none", borderRadius: 4 }}
						/>
					)}
					<Button variant="outlined" size="small" onClick={closeDevFilePreview}>
						Close
					</Button>
				</Box>
			</Dialog>
		</Dialog>
	);
};

export default UpdateModal;
